import { Column, SortingState } from '@tanstack/react-table';
import { isFunction } from 'lodash';
import { X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/button';
import { TableFilter } from '@/components/ui/data-table/filter';
import { ShipmentGrouping } from '@/lib/models/shipment/types';
import { OptionGroup, TableLayout } from '@/types';
import { logEvent } from '@/utils/logger';
import { tv } from '@/utils/styles';

import { DataTableViewOptions } from '../view-options';

export type FilterData = Record<string, OptionGroup[]>;

export interface FilterToolbarProps<TData = any, TValue = any> {
  columns: Column<TData, TValue>[];
  filterData: FilterData;
  onClear?: () => void;
  hasFilters?: boolean;
  shouldShowSaveFilters?: boolean;
  onSaveFilters?: () => void;
  saveFiltersLabel?: string;
  shouldShowResetFilters?: boolean;
  onResetFilters?: () => void;
  sorting?: SortingState;
  onSortingChange?: (layout: SortingState) => void;
  layout?: TableLayout;
  onLayoutChange?: (layout: TableLayout) => void;
  grouping?: ShipmentGrouping;
  onGroupingChange?: (grouping: ShipmentGrouping) => void;
  onEditColumns?: () => void;
}

const FilterToolbar = <TData, TValue>({
  columns,
  filterData,
  onClear,
  hasFilters,
  shouldShowSaveFilters = false,
  onSaveFilters,
  saveFiltersLabel = 'Save',
  shouldShowResetFilters = false,
  onResetFilters,
  sorting,
  onSortingChange,
  layout,
  onLayoutChange,
  grouping,
  onGroupingChange,
  onEditColumns,
}: FilterToolbarProps<TData, TValue>) => {
  const [isLoading, setIsLoading] = useState(false);
  const sortedColumns = columns.sort((a, b) =>
    (a.columnDef.meta?.filterOrder || -1) > (b.columnDef.meta?.filterOrder || -1) ? -1 : 1,
  );

  const handleSaveFilters = async () => {
    if (isFunction(onSaveFilters)) {
      try {
        setIsLoading(true);
        await onSaveFilters();
      } catch (e: any) {
        logEvent.error('Error saving filters', e);
      } finally {
        setIsLoading(false);
      }
    }
  };
  const { base, clearButton, actionWrapper, filterWrapper, filters } = styles();

  return (
    <div className={base()}>
      <div className={filterWrapper()}>
        <div className={filters()}>
          {sortedColumns.map((column, i) => {
            if (column.getCanFilter()) {
              return <TableFilter filterData={filterData} column={column} key={i} />;
            }
            return null;
          })}
          {hasFilters && isFunction(onClear) ? (
            <button type="button" onClick={onClear} className={clearButton()}>
              <X className="size-4" />
              Clear all
            </button>
          ) : null}
        </div>
        <div className={actionWrapper()}>
          {shouldShowResetFilters && isFunction(onResetFilters) ? (
            <Button size="small" variant="primaryLightOutline" type="button" onClick={onResetFilters}>
              Reset
            </Button>
          ) : null}
          {shouldShowSaveFilters && isFunction(onSaveFilters) ? (
            <Button size="small" type="button" onClick={handleSaveFilters} disabled={isLoading} isLoading={isLoading}>
              {saveFiltersLabel}
            </Button>
          ) : null}
        </div>
      </div>
      {layout && (
        <div>
          <DataTableViewOptions
            columns={columns}
            sorting={sorting}
            onSortingChange={onSortingChange}
            layout={layout}
            onLayoutChange={onLayoutChange}
            grouping={grouping}
            onGroupingChange={onGroupingChange}
            onEditColumns={onEditColumns}
          />
        </div>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'my-3 flex w-full flex-col gap-2 md:flex-row',
    filterWrapper: 'flex flex-1 gap-2',
    filters: 'flex flex-wrap items-start gap-2',
    clearButton:
      'flex min-h-[--input-height-small] items-center gap-1 rounded-md p-2 text-baseSm transition-all duration-150 hover:bg-grey-200',
    actionWrapper: 'ml-auto flex gap-2 pt-px',
  },
});

export { FilterToolbar };
